import { Chip, ChipTypeMap } from "@mui/material"
import { OverridableComponent } from "@mui/material/OverridableComponent"
import { styled } from "@mui/material/styles"

const paletteKeys = ["primary", "secondary", "error", "info", "success", "warning"] as const
type PaletteKey = (typeof paletteKeys)[number]

const isPaletteKey = (value: string): value is PaletteKey => (paletteKeys as readonly string[]).includes(value)

type ChipStyledExtraProps = {
  background?: string
}

const ChipStyledRoot = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "background"
})<ChipStyledExtraProps>(({ background, theme }) => {
  let backgroundColor: string | undefined
  let color: string | undefined

  if (!background || background === "default") {
    // Leave MUI Chip defaults; "default" is not a valid CSS color.
  } else if (background === "successLight") {
    backgroundColor = theme.palette.success.main
    color = theme.palette.success.contrastText
  } else if (background === "errorLight") {
    backgroundColor = theme.palette.error.main
    color = theme.palette.error.contrastText
  } else if (isPaletteKey(background)) {
    backgroundColor = theme.palette[background].main
    color = theme.palette[background].contrastText
  } else {
    // Custom hex/rgb/etc. — getContrastText throws on invalid CSS colors.
    backgroundColor = background
    try {
      color = theme.palette.getContrastText(background)
    } catch {
      color = undefined
    }
  }

  return {
    "&.MuiChip-root": {
      ...(backgroundColor && { background: backgroundColor }),
      ...(color && { color }),
      borderRadius: "6px",
      height: "18px",
      "& .MuiChip-label": {
        paddingLeft: 8,
        paddingRight: 8,
        fontSize: theme.typography.subtitle1.fontSize,
        lineHeight: theme.typography.subtitle1.lineHeight,
        fontWeight: 600
      }
    }
  }
})

const ChipStyled = ChipStyledRoot as OverridableComponent<ChipTypeMap<ChipStyledExtraProps, "div">>

export default ChipStyled
